import React, { useEffect, useRef, useState } from "react";
import { toPng } from "html-to-image";
import { fetchMatchRecap } from "../lib/api";
import { AvatarChip } from "./AvatarChip";

/**
 * MatchRecapCardModal — PRO-only shareable PNG of a single completed match.
 * Backend enforces the PRO check via 402; we render a friendly upsell if
 * that comes back. Otherwise users can DOWNLOAD PNG.
 */
export function MatchRecapCardModal({ challengeId, open, onClose, onToast }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const cardRef = useRef(null);

  useEffect(() => {
    if (!open || !challengeId) return;
    setData(null); setErr("");
    fetchMatchRecap(challengeId)
      .then(setData)
      .catch((e) => setErr(e?.response?.data?.detail || "Couldn't load recap."));
  }, [open, challengeId]);

  if (!open) return null;

  const downloadPng = async () => {
    if (!cardRef.current) return;
    setBusy(true);
    try {
      const png = await toPng(cardRef.current, {
        cacheBust: true, pixelRatio: 2, backgroundColor: "#0a0a1f",
      });
      const a = document.createElement("a");
      a.href = png;
      a.download = `league-hub-recap-${challengeId}.png`;
      a.click();
      onToast?.("🖼 Recap saved to downloads.");
    } catch {
      onToast?.("PNG render failed.");
    } finally { setBusy(false); }
  };

  const winnerIsA = data && data.winner_team_id === data.team_a?.team_id;

  return (
    <div className="fixed inset-0 z-[96] bg-black/85 backdrop-blur-md p-4 flex items-start justify-center overflow-y-auto"
         onClick={onClose}
         data-testid="recap-backdrop">
      <div className="max-w-lg w-full my-8" onClick={(e) => e.stopPropagation()}>
        {err ? (
          <div className="glass rounded-3xl p-6 text-center" data-testid="recap-error">
            <p className="text-[10px] tracking-[3px] text-[#14b8a6] font-bold mb-1">
              <i className="fas fa-lock mr-1" />MATCH RECAP — PRO ONLY
            </p>
            <p className="text-sm text-white/75 mt-3">{err}</p>
            <button onClick={onClose}
                    className="mt-5 btn-ghost px-4 py-2 rounded-xl text-[11px] tracking-widest">CLOSE</button>
          </div>
        ) : !data ? (
          <p className="text-center text-white/45 py-12">Loading recap…</p>
        ) : (
          <>
            <div ref={cardRef}
                 data-testid="recap-card"
                 className="rounded-3xl p-6 bg-gradient-to-br from-[#04081e] via-[#08182f] to-[#1a073b] border-2 border-[#14b8a6]/40 shadow-[0_30px_100px_rgba(20, 184, 166,0.35)]">
              <div className="flex items-center justify-between mb-3">
                <p className="text-[10px] tracking-[4px] text-[#a3a3ac] font-bold">
                  LEAGUE HUB · MATCH RECAP
                </p>
                {data.league?.name && (
                  <span className="text-[10px] tracking-widest text-white/55">
                    {data.league.name}
                  </span>
                )}
              </div>

              <div className="grid grid-cols-3 items-center gap-3 py-2">
                <SideBlock team={data.team_a} score={data.score_a} isWinner={winnerIsA} accent="cyan" />
                <div className="text-center">
                  <p className="font-display text-2xl text-white/50">VS</p>
                  <p className="text-[9px] tracking-widest text-[#14b8a6] mt-1">FINAL</p>
                </div>
                <SideBlock team={data.team_b} score={data.score_b} isWinner={!winnerIsA} accent="pink" />
              </div>

              <p className="mt-4 text-center text-[10px] tracking-[3px] text-white/45">
                {data.completed_at ? new Date(data.completed_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : ""}
              </p>
              <p className="mt-3 text-center text-[10px] tracking-[3px] text-white/35">
                leaguehub.co · neon leagues, epic wins
              </p>
            </div>

            <div className="mt-5 flex flex-wrap justify-center gap-2">
              <button type="button" onClick={onClose}
                      data-testid="recap-close"
                      className="text-[11px] tracking-widest px-4 py-2 rounded-xl btn-ghost">
                CLOSE
              </button>
              <button type="button" onClick={downloadPng} disabled={busy}
                      data-testid="recap-download-btn"
                      className="text-[11px] tracking-widest px-5 py-2 rounded-xl btn-neon disabled:opacity-50">
                {busy ? "…" : <><i className="fas fa-download mr-1" />DOWNLOAD PNG</>}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function SideBlock({ team, score, isWinner, accent }) {
  const accentClass = accent === "cyan" ? "text-[#14b8a6]" : "text-[#14b8a6]";
  return (
    <div className="text-center">
      <div className="flex items-center justify-center">
        <AvatarChip avatarId={team?.avatar_id} pfpUrl={team?.pfp_url} size={72} />
      </div>
      <p className={`mt-2 text-xs font-bold ${accentClass} truncate`}>{team?.name || "—"}</p>
      <p className={`font-display text-4xl tabular-nums mt-1 ${isWinner ? "text-[#14b8a6]" : "text-white/55"}`}>
        {score}
      </p>
      {isWinner && (
        <p className="text-[9px] tracking-widest text-[#14b8a6] mt-0.5">WINNER 🏆</p>
      )}
    </div>
  );
}

export default MatchRecapCardModal;
